import PatchRuleEditor from '../components/PatchRuleEditor';
import {romanLevel} from '../services/patchProgression';
import React, { useEffect, useRef, useState } from "react";
import { useGuest } from "../services/GuestContext";
import {
  badgeRequest,
  BadgeDefinition,
  PatchProgressionLevel,
} from "../services/badgeApi";
import { useSettingsChanges } from "../services/settingsChanges";
import BadgeArtwork from "../components/BadgeArtwork";
import {usePresentationHold} from '../services/usePresentation';
import {useBadgeScope} from '../services/useBadgeScope';
import BadgeArtworkInput from '../components/BadgeArtworkInput';
import ManualBadgeAward from '../components/ManualBadgeAward';
import HiddenPatchSettings from '../components/HiddenPatchSettings';
import WelcomePatchSettings from '../components/WelcomePatchSettings';
import {PatchLevel,patchLevels,patchLevelLabel} from '../services/patchLevels';
import {useSearchParams} from 'react-router-dom';
const blank: Partial<BadgeDefinition> = {
  name: "",
  description: "",
  image: "",
  hidden: false,
  presentation_kind: 'patch',
  story: '',
  points: 0,
  level: 'common',
  status: "published",
  rule: { type: "manual" },
};
interface BackfillPreview {
  evidence_notice?: string;
  digest: string;
  count: number;
  points: number;
  operation_id: string;
  enrollment_count: number;
  members: {
    email: string;
    name: string;
    visit_ids: string[];
    summary?: { count: number; seconds?: number };
  }[];
  unresolved?: { projects: unknown[]; work: unknown[]; photos: unknown[] };
  candidates: { email: string; name: string; source: { count: number } }[];
}
export default function BadgeSettings() {
  const [searchParams,setSearchParams]=useSearchParams();
  const recipient=searchParams.get('recipient')||'';
  const requestedPatch=searchParams.get('patch')||'';
  const [historyLevel,setHistoryLevel]=useState(1);
  const [editing,setEditing]=useState(false),[panel,setPanel]=useState<'edit'|'award'>('edit');
  const { farm } = useGuest();
  const scope=useBadgeScope(),currentScope=useRef(scope);currentScope.current=scope;
  const [loadedScope,setLoadedScope]=useState(''),[artworkBusy,setArtworkBusy]=useState(false);
  const [templateDirty,setTemplateDirty]=useState(false);
  const [backDirty,setBackDirty]=useState(false);
  const [welcomeDirty,setWelcomeDirty]=useState(false);
  const [badges, setBadges] = useState<BadgeDefinition[]>([]),
    [places, setPlaces] = useState<{ map_feature_id: string; label: string }[]>(
      [],
    );
  const [draft, setDraft] = useState<Partial<BadgeDefinition>>(blank),
    [baseline, setBaseline] = useState(JSON.stringify(blank)),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [message, setMessage] = useState("");
  const [projects,setProjects]=useState<{id:string;name:string;status?:string}[]>([]);
  const [preview, setPreview] = useState<Partial<BadgeDefinition> | null>(null),
    [backfill, setBackfill] = useState<BackfillPreview | null>(null);
  const dirty = JSON.stringify(draft) !== baseline;
  useSettingsChanges(loadedScope===scope&&(dirty||templateDirty||backDirty||welcomeDirty));
  usePresentationHold(loadedScope===scope&&(dirty || busy || artworkBusy || welcomeDirty), 'Back to recognition settings');
  const load = async (current:()=>boolean) => {
    if (!farm) return;
    const [data, poi] = await Promise.all([
      badgeRequest<{definitions:BadgeDefinition[]}>(farm.farm_id,'/definitions'),
      badgeRequest<{ places: typeof places;projects?:typeof projects }>(farm.farm_id, "/places"),
    ]);
    if(!current())return;
    setBadges(data.definitions);
    setPlaces(poi.places);
    setProjects(poi.projects||[]);
  };
  useEffect(() => {
    let current = true;
    setBadges([]);
    setEditing(false);setPanel('edit');
    setPlaces([]);
    setProjects([]);
    setDraft(blank);
    setBaseline(JSON.stringify(blank));
    setPreview(null);
    setBackfill(null);
    setError('');setMessage('');setBusy(false);setArtworkBusy(false);setTemplateDirty(false);setBackDirty(false);setWelcomeDirty(false);setLoadedScope('');
    if (farm)
      void Promise.all([
        badgeRequest<{definitions:BadgeDefinition[]}>(farm.farm_id,'/definitions'),
        badgeRequest<{ places: typeof places;projects?:typeof projects }>(farm.farm_id, "/places"),
      ])
        .then(([data, poi]) => {
          if (current&&currentScope.current===scope) {
            setBadges(data.definitions);
            setPlaces(poi.places);
            setProjects(poi.projects||[]);
            setLoadedScope(scope);
            if(requestedPatch){
              const selected=data.definitions.find(b=>b.badge_id===requestedPatch);
              if(selected){setDraft(selected);setBaseline(JSON.stringify(selected));setEditing(true);setPanel('edit');}
              else setError('This patch is not available in this property.');
            }
          }
        })
        .catch((e) => {
          if (current&&currentScope.current===scope){setError(e.message);setLoadedScope(scope);}
        });
    return () => {
      current = false;
    };
  }, [scope,requestedPatch]);
  const select = (value: Partial<BadgeDefinition>) => {
    if (dirty && !window.confirm("Discard unsaved badge changes?")) return;
    setHistoryLevel(1);
    setDraft(value);
    setEditing(true);setPanel(recipient&&value.badge_id?'award':'edit');
    setBaseline(JSON.stringify(value));
    setPreview(null);
    setBackfill(null);
    setError("");
    setMessage("");
  };
  const patch = (value: Partial<BadgeDefinition>) => {
    setDraft((d) => ({ ...d, presentation_kind:'patch', ...value }));
    setPreview(null);
    setBackfill(null);
    setMessage("");
  };
  const copyToNew = () => {
    if(!draft.badge_id||dirty||busy||artworkBusy)return;
    const copied:Partial<BadgeDefinition>={
      ...blank,
      name:`${(draft.name||'Patch').slice(0,73).trimEnd()} (copy)`,
      description:draft.description||'',
      image:draft.image||'',
      story:draft.story||'',
      hidden:!!draft.hidden,
      level:draft.level||'common',
      rule:structuredClone(draft.rule||blank.rule!),
      progression_levels:draft.progression_levels?structuredClone(draft.progression_levels):null,
    };
    setDraft(copied);setBaseline(JSON.stringify(blank));setPanel('edit');
    setPreview(null);setBackfill(null);setError('');
    setMessage('Copied to a new patch. Adjust its name and settings, then save.');
  };
  const run = async (fn: (current:()=>boolean) => Promise<void>) => {
    const current=()=>currentScope.current===scope;
    setBusy(true);
    setError("");
    try {
      await fn(current);
    } catch (e) {
      if(current())setError((e as Error).message);
    } finally {
      if(current())setBusy(false);
    }
  };
  const save = () =>
    run(async current => {
      if (!farm) return;
      const saved = await badgeRequest<BadgeDefinition>(
        farm.farm_id,
        "/definitions" + (draft.badge_id ? "/" + draft.badge_id : ""),
        draft.badge_id ? "PUT" : "POST",
        draft,
      );
      if(!current())return;
      setDraft(saved);
      setBaseline(JSON.stringify(saved));
      setPreview(null);
      setBackfill(null);
      setMessage(saved.status==='published'?'Patch saved and available to award.':'Patch saved.');
      if(!draft.badge_id&&requestedPatch){
        const next=new URLSearchParams(searchParams);next.set('patch',saved.badge_id);
        setSearchParams(next,{replace:true});
      }
      await load(current);
    });
  const rule = draft.progression_levels?.find(level=>level.number===historyLevel)?.rule || draft.rule || blank.rule!;
  const changeLevel=(index:number,changes:Partial<PatchProgressionLevel>)=>patch({progression_levels:draft.progression_levels!.map((level,i)=>i===index?{...level,...changes}:level)});
  const deletePatch = () => {
    if(!farm||!draft.badge_id||busy||artworkBusy)return;
    if(!window.confirm(`Delete “${draft.name}” permanently? This removes it and all its awards from everyone's collections. This cannot be undone.`))return;
    void run(async current=>{
      await badgeRequest(farm.farm_id,'/definitions/'+draft.badge_id,'DELETE',{version:draft.version});
      if(!current())return;
      setEditing(false);setDraft(blank);setBaseline(JSON.stringify(blank));setPreview(null);setBackfill(null);
      setMessage('Patch deleted from all collections.');
      if(requestedPatch){const next=new URLSearchParams(searchParams);next.delete('patch');setSearchParams(next,{replace:true});}
      await load(current);
    });
  };
  const input = "block w-full border rounded-lg p-3 mt-1";
  if(loadedScope!==scope)return <p role="status">Loading recognition settings…</p>;
  return (
    <section className="space-y-5">
      <header>
        <h2 className="text-xl font-semibold">{editing?draft.name||'New patch':'Manage patches'}</h2>
        <p className="text-sm text-gray-500 mt-2">
          {editing?'Edit this patch or award it to someone.':'Choose a patch to edit or award. Automatic earning and admin awards can both be used for the same patch.'}
        </p>
      </header>
      {recipient&&<p className="text-sm text-gray-600">{editing?'Recipient: ':'Choose a patch to award to '}{recipient}.</p>}
      {error && (
        <p role="alert" className="text-red-700">
          {error}
        </p>
      )}
      {message && (
        <p role="status" className="text-usda-700">
          {message}
        </p>
      )}
      {!editing?<>
        <button className="bg-usda-700 text-white px-4 py-2 rounded-lg" disabled={busy||artworkBusy||templateDirty||backDirty||welcomeDirty} onClick={()=>select(blank)}>New patch</button>
        {!badges.length&&<p className="text-sm text-gray-500">No patches yet. Create one with artwork and a reason it’s earned.</p>}
        <div className="grid sm:grid-cols-2 gap-3">{badges.map(b=><button key={b.badge_id} disabled={busy||artworkBusy||templateDirty||backDirty||welcomeDirty} onClick={()=>select(b)} className="bg-white border rounded-xl p-4 text-left flex gap-4 items-center hover:border-usda-600">
          <BadgeArtwork image={b.image} name={b.name} property={farm?.name}/>
          <span className="min-w-0"><span className="block font-semibold break-words">{b.name}</span><span className="block text-xs text-gray-500 mt-1">{b.status==='retired'?'Retired · history kept':''}</span><span className="block text-sm text-gray-600 mt-2 break-words">{b.description}</span></span>
        </button>)}</div>
        {farm&&<><HiddenPatchSettings farmId={farm.farm_id} onDirtyChange={setTemplateDirty}/><HiddenPatchSettings kind="back" farmId={farm.farm_id} onDirtyChange={setBackDirty}/><WelcomePatchSettings key={scope} patches={badges} disabled={busy||artworkBusy} onDirtyChange={setWelcomeDirty}/></>}
      </>:<>
        <button disabled={busy||artworkBusy} className="text-sm text-usda-700 underline" onClick={()=>{if(!dirty||window.confirm('Discard unsaved patch changes?')){setEditing(false);setDraft(blank);setBaseline(JSON.stringify(blank));setPreview(null);setBackfill(null);}}}>Back to patches</button>
        <div className="flex gap-2" role="group" aria-label="Patch actions"><button aria-pressed={panel==='edit'} className={'border rounded-lg px-4 py-3 '+(panel==='edit'?'bg-usda-700 text-white':'bg-white')} onClick={()=>setPanel('edit')}>Edit</button>{draft.badge_id&&<button aria-pressed={panel==='award'} className={'border rounded-lg px-4 py-3 '+(panel==='award'?'bg-usda-700 text-white':'bg-white')} onClick={()=>setPanel('award')}>Award</button>}</div>
        {draft.badge_id&&<div><button disabled={dirty||busy||artworkBusy} onClick={copyToNew} className="border rounded-lg bg-white px-4 py-2">Copy to new patch</button>{dirty&&<p className="text-xs text-gray-500 mt-2">Save your changes before copying this patch.</p>}</div>}
        {panel==='award'&&<>
          {dirty?<p className="text-sm bg-amber-50 rounded-xl p-4">Save your changes before awarding this patch.</p>:draft.status!=='published'?<div className="bg-white border rounded-xl p-5 space-y-3"><p>This patch is retired. Clear Retired and save to allow new awards; existing awards are preserved.</p><button className="border rounded-lg px-4 py-2" onClick={()=>setPanel('edit')}>Edit patch</button></div>:farm&&<ManualBadgeAward key={scope+':'+draft.badge_id+':'+draft.version} farmId={farm.farm_id} badge={draft as BadgeDefinition} initialRecipient={recipient} disabled={busy||artworkBusy}/>}
        </>}
      </>}
      {editing&&panel==='edit'&&<fieldset disabled={busy||artworkBusy} className="bg-white border rounded-2xl p-5 space-y-4">
        <p className="text-sm text-gray-600">{draft.status==='retired'?'Retired: no new awards. Existing earned patches stay in their collections.':!draft.badge_id?'Save this patch to make it available to earn or award.':rule.type==='manual'?'Available for an admin to award.':'Members can earn this patch automatically, or an admin can award it directly.'}</p>
        <label className="block text-sm font-medium">
          Name
          <input
            className={input}
            maxLength={80}
            value={draft.name || ""}
            onChange={(e) => patch({ name: e.target.value })}
          />
        </label>
        <label className="flex items-start gap-3 text-sm">
          <input type="checkbox" className="mt-1 h-5 w-5 shrink-0" checked={!!draft.progression_levels} onChange={e=>{
            setHistoryLevel(1);
            if(e.target.checked)patch({progression_levels:[1,2,3,4].map(number=>({number,description:number===1?draft.description||'':'',image:number===1?draft.image||'':'',rarity:number===1?draft.level||'common':'common',rule:number===1?structuredClone(draft.rule||blank.rule!):{type:'manual'}}))});
            else {const first=draft.progression_levels![0];patch({description:first.description,image:first.image,level:first.rarity,rule:first.rule,progression_levels:null});}
          }}/>
          <span><span className="block font-medium">Leveled patch</span><span className="block text-gray-500 mt-1">Four independently earned levels, I–IV. Collections show the highest earned level.</span></span>
        </label>
        {draft.progression_levels?.map((level,index)=><section key={level.number} aria-label={'Level '+romanLevel(level.number)} className="border rounded-xl p-4 space-y-4">
          <h3 className="font-semibold">Level {romanLevel(level.number)}</h3>
          <label className="block text-sm font-medium">Why it’s earned<textarea className={input} maxLength={500} value={level.description} onChange={e=>changeLevel(index,{description:e.target.value})}/></label>
          <BadgeArtworkInput value={level.image} disabled={busy||artworkBusy} onChange={image=>changeLevel(index,{image})} onBusyChange={setArtworkBusy}/>
          <label className="block text-sm font-medium">Rarity<select className={input} value={level.rarity} onChange={e=>changeLevel(index,{rarity:e.target.value as PatchLevel})}>{patchLevels.map(rarity=><option key={rarity} value={rarity}>{patchLevelLabel(rarity)}</option>)}</select></label>
          <PatchRuleEditor rule={level.rule} places={places} projects={projects} onChange={rule=>changeLevel(index,{rule})}/>
        </section>)}
        {!draft.progression_levels&&<label className="block text-sm font-medium">
          Why it’s earned
          <textarea
            className={input}
            maxLength={500}
            value={draft.description || ""}
            onChange={(e) => patch({ description: e.target.value })}
          />
        </label>
        }
        <label className="block text-sm font-medium">
          Property story (optional)
          <textarea className={input} rows={3} maxLength={1000} value={draft.story || ''} onChange={e=>patch({story:e.target.value})}/>
          <span className="text-xs font-normal text-gray-500">Use actual context or history. This appears in the reveal and earned collection.</span>
        </label>
        {!draft.progression_levels&&<BadgeArtworkInput key={scope+':'+(draft.badge_id||'new')} value={draft.image||''} disabled={busy} onChange={image=>patch({image})} onBusyChange={setArtworkBusy}/>}
        <label className="flex items-start gap-3 text-sm">
          <input type="checkbox" className="mt-1 h-5 w-5 shrink-0" checked={!!draft.hidden} onChange={e=>patch({hidden:e.target.checked})}/>
          <span><span className="block font-medium">Hidden patch</span><span className="block text-gray-500 mt-1">Hide its name and artwork until earned. Its stats remain visible.</span></span>
        </label>
        <div className="grid sm:grid-cols-2 gap-4">
          {!draft.progression_levels&&<label className="block text-sm font-medium">
            Rarity
            <select className={input} value={draft.level||'common'} onChange={e=>patch({level:e.target.value as PatchLevel})}>
              {patchLevels.map(level=><option key={level} value={level}>{patchLevelLabel(level)}</option>)}
            </select>
          </label>
          }
          <label className="flex items-start gap-3 text-sm self-center">
            <input type="checkbox" className="mt-1 h-5 w-5 shrink-0" checked={draft.status==='retired'} onChange={e=>patch({status:e.target.checked?'retired':'published'})}/>
            <span><span className="block font-medium">Retired</span><span className="block text-gray-500 mt-1">Stop new awards. Keep earned history.</span></span>
          </label>
        </div>
        {!draft.progression_levels&&<PatchRuleEditor rule={draft.rule||blank.rule!} places={places} projects={projects} onChange={rule=>patch({rule})}/>}

        <div className="flex flex-wrap gap-3">
          <button
            disabled={busy || artworkBusy || !farm}
            className="border rounded-lg px-4 py-2"
            onClick={() =>
              void run(async current => {
                if (farm){
                  const result=await badgeRequest<{ badge: BadgeDefinition }>(
                        farm.farm_id,
                        "/preview",
                        "POST",
                        draft,
                      );
                  if(current())setPreview(result.badge);
                }
              })
            }
          >
            Preview & validate
          </button>
          <button
            disabled={busy || artworkBusy || !dirty || !farm}
            className="bg-usda-700 text-white px-4 py-2 rounded-lg disabled:opacity-50"
            onClick={() => void save()}
          >
            Save patch
          </button>
        </div>
        {preview && (
          <article className="bg-amber-50 rounded-xl p-5 space-y-3">
            {preview.progression_levels?.map(level=><div key={level.number} className="flex gap-4 items-center"><BadgeArtwork image={level.image} name={(preview.name||'')+' '+romanLevel(level.number)}/><div><h4 className="font-semibold">{preview.name} {romanLevel(level.number)}</h4><p>{level.description}</p><p className="text-sm">{patchLevelLabel(level.rarity)}</p></div></div>)}
            {!preview.progression_levels&&<BadgeArtwork
              image={preview.image || ""}
              name={preview.name || ""}
              property={farm?.name}
              medal={preview.presentation_kind==='medal'}
              large
            />}
            <h3 className="font-semibold">{preview.name}</h3>
            <p>{preview.description}</p>
            {preview.story&&<p className="text-gray-600 whitespace-pre-wrap">{preview.story}</p>}
            <p className="text-xs text-gray-500">Preview only · {patchLevelLabel(preview.level)} patch</p>
          </article>
        )}
        {draft.badge_id&&<div className="border-t pt-4"><button disabled={busy||artworkBusy} className="border border-red-300 text-red-700 rounded-lg px-4 py-2 disabled:opacity-50" onClick={deletePatch}>Delete patch</button><p className="text-xs text-gray-500 mt-2">Permanently remove this patch and its awards from all collections.</p></div>}
      </fieldset>}
      {editing&&panel==='edit'&&draft.badge_id &&
        draft.status === "published" &&
        (draft.progression_levels || (rule.type !== "poi_arrival" && rule.type!=='manual')) && (
          <details className="border rounded-2xl p-5 bg-white space-y-3">
            <summary className="font-semibold cursor-pointer">Award from recorded history</summary>
            <p className="text-sm text-gray-500">
              Select a level with a stay or contribution rule. Preview verified recorded evidence before awarding historical
              badges. Unresolved records remain excluded.
            </p>
            {draft.progression_levels&&<label className="block text-sm">Level<select className={input} value={historyLevel} disabled={busy||dirty} onChange={e=>{setHistoryLevel(Number(e.target.value));setBackfill(null);}}>{[1,2,3,4].map(number=><option key={number} value={number}>{romanLevel(number)}</option>)}</select></label>}
            <button
              disabled={busy || artworkBusy || dirty || rule.type==='manual' || rule.type==='poi_arrival'}
              className="underline text-usda-700 disabled:opacity-50"
              onClick={() =>
                void run(async current => {
                  if (farm) {
                    const result = await badgeRequest<BackfillPreview>(
                      farm.farm_id,
                      "/definitions/" + draft.badge_id + "/backfill-preview",
                      "POST",
                      {progression_level:historyLevel},
                    );
                    if(!current())return;
                    setBackfill({
                      ...result,
                      operation_id: crypto.randomUUID(),
                    });
                  }
                })
              }
            >
              Preview backfill
            </button>
            {backfill && (
              <>
                <p>
                  {backfill.count} new awards
                </p>
                <p className="text-sm text-gray-600">
                  Recorded history will count toward this badge for{" "}
                  {backfill.enrollment_count} members without an existing award.
                </p>
                <ul className="text-sm">
                  {backfill.evidence_notice&&<li className="text-gray-600">{backfill.evidence_notice}</li>}
                  {backfill.members.map((c) => (
                    <li key={c.email}>
                      {c.name} ·{" "}
                      {c.summary
                        ? rule.type === "work_minutes"
                          ? `${c.summary.seconds} approved seconds`
                          : `${c.summary.count} qualifying contributions`
                        : `${c.visit_ids.length} recorded stays`}
                    </li>
                  ))}
                </ul>
                {backfill.unresolved && (
                  <p className="text-sm">
                    Unresolved: {backfill.unresolved.projects.length} projects,{" "}
                    {backfill.unresolved.work.length} work logs,{" "}
                    {backfill.unresolved.photos.length} photos. Review
                    attribution before including them.
                  </p>
                )}
                <button
                  disabled={busy || !backfill.enrollment_count}
                  className="bg-usda-700 text-white rounded-lg px-4 py-2 disabled:opacity-50"
                  onClick={() =>
                    void run(async current => {
                      if (!farm) return;
                      const result = await badgeRequest<{ count: number }>(
                        farm.farm_id,
                        "/definitions/" + draft.badge_id + "/backfill",
                        "POST",
                        {
                          progression_level:historyLevel,
                          digest: backfill.digest,
                          operation_id: backfill.operation_id,
                        },
                      );
                      if(!current())return;
                      setBackfill(null);
                      setMessage(`${result.count} historical awards saved.`);
                    })
                  }
                >
                  Apply previewed backfill
                </button>
              </>
            )}
          </details>
        )}
    </section>
  );
}
